import { FunctionComponent } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import styled from 'styled-components'
import { object, string as yupString, ref } from 'yup'

import { ErrorCode } from 'src/Redux/types'
import { changeArtistPassword } from 'src/Redux/settings-process/artistPassword/api-actions'
import { PrimaryInput } from 'src/Components/PrimaryInput'
import { PrimaryAlert } from 'src/Components/PrimaryAlert'
import { useAppDispatch } from 'src/Hooks/redux'
import { getIsSuccessChangedPassword } from 'src/Redux/settings-process/artistPassword/selectors'
import { getIsArtist } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { getHasPassword } from 'src/Redux/auth-process/userSlice/selectors'
import { refetchUsersMe } from 'src/Redux/userInfoApi'
import { CTAButton } from 'src/Components/Buttons/CTAButton'

import {
  AlertWrapper,
  Form,
  ChangePasswordFormInput,
  ChangePasswordFormGroup,
  ChangePasswordFormWrapper,
} from './styles'

const PASSWORD_MIN_LENGTH = 8
const PASSWORD_MAX_LENGTH = 32
const PASSWORD_RULE_MESSAGE =
  'Password must be 8–32 characters and contain at least one uppercase letter, one lowercase letter, and one number'

const ChangePasswordSchema = object({
  currentPassword: yupString().when('$hasPassword', {
    is: true,
    then: (schema) =>
      schema.required('Current password field should not be empty'),
  }),
  password: yupString()
    .required('Password field should not be empty')
    .min(PASSWORD_MIN_LENGTH, PASSWORD_RULE_MESSAGE)
    .max(PASSWORD_MAX_LENGTH, PASSWORD_RULE_MESSAGE)
    .matches(/[A-Z]/, PASSWORD_RULE_MESSAGE)
    .matches(/[a-z]/, PASSWORD_RULE_MESSAGE)
    .matches(/\d/, PASSWORD_RULE_MESSAGE),
  confirm: yupString()
    .required('You should confirm your new password')
    .oneOf(
      [ref('password')],
      'The password and confirmation password do not match',
    ),
}).required()

const StyledCTAButton = styled(CTAButton)`
  width: fit-content;
  margin: 0 auto;
  padding-left: 40px;
  padding-right: 40px;
`

const PasswordInput = styled(PrimaryInput)`
  -webkit-text-security: disc;
`

interface FormState {
  currentPassword?: string
  password: string
  confirm: string
}

const ChangePassword: FunctionComponent = () => {
  const isArtist = useSelector(getIsArtist)
  const hasPassword = useSelector(getHasPassword)
  const isSuccess = useSelector(getIsSuccessChangedPassword)
  const dispatch = useAppDispatch()

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormState>({
    mode: 'onChange',
    resolver: yupResolver(ChangePasswordSchema),
    context: { hasPassword },
  })

  const onSubmit = async (data: FormState): Promise<void> => {
    if (isArtist) {
      const result = await dispatch(
        changeArtistPassword({
          ...(hasPassword && { currentPassword: data.currentPassword }),
          newPassword: data.password,
        }),
      )
      if (changeArtistPassword.rejected.match(result)) {
        const payload = result.payload as { message: string; status: number }
        if (payload?.status === ErrorCode.Conflict) {
          setError('currentPassword', {
            message: 'Current password is incorrect',
          })
        } else if (payload?.message) {
          setError('password', { message: payload.message })
        } else {
          setError('password', { message: 'Something went wrong' })
        }
        return
      }
      void dispatch(refetchUsersMe())
    }
    reset()
  }

  return (
    <>
      {isSuccess && (
        <AlertWrapper>
          <PrimaryAlert type='success' hasCross={false}>
            Your password has been changed.
          </PrimaryAlert>
        </AlertWrapper>
      )}
      <ChangePasswordFormWrapper>
        <Form data-testid='change-password' onSubmit={handleSubmit(onSubmit)}>
          <ChangePasswordFormGroup>
            {hasPassword && (
              <ChangePasswordFormInput>
                <Controller
                  name='currentPassword'
                  control={control}
                  defaultValue=''
                  render={({ field }) => (
                    <PasswordInput
                      {...field}
                      placeholder='Enter current password'
                      error={errors.currentPassword?.message}
                    />
                  )}
                />
              </ChangePasswordFormInput>
            )}
            <ChangePasswordFormInput>
              <Controller
                name='password'
                control={control}
                defaultValue=''
                render={({ field }) => (
                  <PasswordInput
                    {...field}
                    placeholder='Enter new password'
                    error={errors.password?.message}
                  />
                )}
              />
            </ChangePasswordFormInput>
            <ChangePasswordFormInput>
              <Controller
                name='confirm'
                control={control}
                defaultValue=''
                render={({ field }) => (
                  <PasswordInput
                    {...field}
                    placeholder='Confirm new password'
                    error={errors.confirm?.message}
                  />
                )}
              />
            </ChangePasswordFormInput>
          </ChangePasswordFormGroup>

          <StyledCTAButton type='submit' disabled={!isValid || isSubmitting}>
            Update password
          </StyledCTAButton>
        </Form>
      </ChangePasswordFormWrapper>
    </>
  )
}

export default ChangePassword
